import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPage, AdminPageHeader } from "@/components/admin-ui";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DmError,
  FanPassConfigRequest,
  artistHubEnabled,
  dmChannelConfigGet,
  fanPassConfigGet,
  fanPassConfigRequestApprove,
  fanPassConfigRequestList,
  fanPassConfigRequestReject,
  fanProductStatus,
} from "@/services/firebase/artistHub";
import {
  FAN_PASS_REQUEST_STATUS_LABELS,
  FAN_PASS_REQUEST_STATUS_ORDER,
  FanPassChangeLine,
  FanPassCurrentValues,
  FanPassRequestStatus,
  REJECT_REASON_MAX,
  fanPassChangeSummary,
  fanPassRequestStatusLabel,
  fanPassRequestStatusVariant,
  formatManualSteps,
  rejectReasonProblem,
} from "@/lib/creator";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
const isStateConflict = (e: unknown) => e instanceof DmError && e.code === "FAN_PASS_REQUEST_STATE";
const artistHref = (artistId: string) => `/actors/${encodeURIComponent(artistId)}/edit`;
const STALE_MS = 60_000;

/**
 * Current values for the change summary — the same reads (and query keys) as ArtistFanPassPanel /
 * ArtistFanProductSection / ArtistDmPanel, so the artist page and this queue share the cache.
 * A read that fails just leaves that field unknown ("—").
 */
function useCurrentFanPassValues(artistId: string, enabled: boolean): { values: FanPassCurrentValues; loading: boolean } {
  const config = useQuery({ queryKey: ["artist-fan-pass-config", artistId], queryFn: () => fanPassConfigGet(artistId), retry: false, enabled, staleTime: STALE_MS });
  const product = useQuery({ queryKey: ["artist-fan-product", artistId], queryFn: () => fanProductStatus(artistId), retry: false, enabled, staleTime: STALE_MS });
  const dm = useQuery({ queryKey: ["dm-config", artistId], queryFn: () => dmChannelConfigGet(artistId), retry: false, enabled, staleTime: STALE_MS });
  const values: FanPassCurrentValues = {};
  if (product.data) {
    values.priceTier = product.data.provisioned ? product.data.priceTier : null;
    values.saleEnabled = product.data.provisioned ? product.data.saleEnabled : false;
  }
  if (config.data) values.benefits = config.data.benefits;
  if (dm.data) values.welcomeText = dm.data.welcome?.text ?? null;
  return { values, loading: enabled && (config.isPending || product.isPending || dm.isPending) };
}

/**
 * C-05: 鐵粉月卡自助申請審核佇列（B-43, contract v2.7; staff only）.
 *   fanPassConfigRequestList {status, cursor, limit} → 待審／已核准／需人工／已拒絕 tabs
 *   fanPassConfigRequestApprove {requestId} → approved（已自動套用）或 approved_manual（回 manualSteps 需人工完成）
 *   fanPassConfigRequestReject {requestId, reason}
 * `?artistId=` filters the loaded rows client side (link from ArtistFanPassPanel).
 * Design: docs/v3/工作單/ReelPlus-v3-創作者帳號與經營中心UIUX重新設計-20261016.md（月卡自助）.
 */
export default function FanPassRequestsPage() {
  const [params, setParams] = useSearchParams();
  const artistFilter = params.get("artistId") ?? "";
  const [status, setStatus] = useState<FanPassRequestStatus>("pending");
  const [approving, setApproving] = useState<FanPassConfigRequest | null>(null);
  const [rejecting, setRejecting] = useState<FanPassConfigRequest | null>(null);
  const [notice, setNotice] = useState("");
  const list = useInfiniteQuery({
    queryKey: ["fan-pass-requests", status],
    queryFn: ({ pageParam }) => fanPassConfigRequestList({ status, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: artistHubEnabled,
    retry: false,
  });

  if (!artistHubEnabled) {
    return (
      <AdminPage>
        <AdminPageHeader title="月卡申請" />
        <p>此環境尚未開放藝人專區（鐵粉月卡申請）。</p>
      </AdminPage>
    );
  }
  const all = list.data?.pages.flatMap((p) => p.items) ?? [];
  const items = artistFilter ? all.filter((r) => r.artistId === artistFilter) : all;
  const notDeployed = list.error instanceof DmError && list.error.status === 404 && !list.error.code;
  return (
    <AdminPage maxWidth="wide">
      <AdminPageHeader
        title="月卡申請"
        subtitle="創作者在 App 送出的鐵粉月卡變更（價格檔位、開賣、權益、歡迎訊息）；核准後自動套用，無法自動完成的步驟會列出需人工處理"
        loading={list.isFetching}
        onRefresh={() => void list.refetch()}
      />
      <Tabs
        value={status}
        onValueChange={(v) => {
          setStatus(v as FanPassRequestStatus);
          setNotice("");
        }}
        className="mb-4"
      >
        <TabsList className="flex-wrap">
          {FAN_PASS_REQUEST_STATUS_ORDER.map((s) => (
            <TabsTrigger key={s} value={s}>
              {FAN_PASS_REQUEST_STATUS_LABELS[s]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {artistFilter && (
        <p className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          只顯示藝人 <code className="break-all text-xs">{artistFilter}</code>（已載入的資料）
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              params.delete("artistId");
              setParams(params, { replace: true });
            }}
          >
            顯示全部
          </Button>
        </p>
      )}
      {notice && (
        <p role="status" className="mb-4 text-sm text-success">
          {notice}
        </p>
      )}
      {list.isError &&
        (notDeployed ? (
          <p role="status" className="text-sm text-muted-foreground">
            後端尚未提供 fanPassConfigRequestList（B-43），部署後即可使用。
          </p>
        ) : (
          <p role="alert" className="text-destructive">
            {errorText(list.error)}
          </p>
        ))}
      {list.isPending && <p>讀取申請…</p>}
      {list.isSuccess && items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {status === "pending" ? "目前沒有待審的月卡申請。" : `沒有${FAN_PASS_REQUEST_STATUS_LABELS[status]}的月卡申請。`}
        </p>
      )}
      <ul className="space-y-3">
        {items.map((r) => (
          <li key={r.requestId}>
            <RequestCard
              request={r}
              onApprove={() => {
                setNotice("");
                setApproving(r);
              }}
              onReject={() => {
                setNotice("");
                setRejecting(r);
              }}
            />
          </li>
        ))}
      </ul>
      {list.hasNextPage && (
        <Button className="mt-4" variant="outline" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {list.isFetchingNextPage ? "載入中…" : "載入更多"}
        </Button>
      )}
      {approving && (
        <ApproveDialog
          request={approving}
          onClose={() => setApproving(null)}
          onDone={(text) => {
            setApproving(null);
            setNotice(text);
          }}
        />
      )}
      {rejecting && (
        <RejectDialog
          request={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            setNotice(`已拒絕「${rejecting.artistName || rejecting.artistId}」的月卡申請`);
          }}
        />
      )}
    </AdminPage>
  );
}

function ChangeSummary({ lines, showFrom }: { lines: FanPassChangeLine[]; showFrom: boolean }) {
  if (lines.length === 0) return <p className="text-sm text-muted-foreground">（申請沒有包含任何欄位）</p>;
  return (
    <dl className="grid gap-x-3 gap-y-1 text-sm sm:grid-cols-[6rem_1fr]">
      {lines.map((l) => (
        <div key={l.field} className="contents">
          <dt className="text-muted-foreground">{l.label}</dt>
          <dd className="min-w-0 break-words">
            {showFrom ? (
              <>
                <span className={l.changed ? "text-muted-foreground line-through" : "text-muted-foreground"}>{l.from}</span>
                <span aria-label="改為"> → </span>
                <span className={l.changed ? "font-semibold" : undefined}>{l.to}</span>
                {!l.changed && <span className="ml-1 text-xs text-muted-foreground">（未變）</span>}
              </>
            ) : (
              <span>{l.to}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ManualSteps({ steps, heading }: { steps: string[]; heading: string }) {
  const [copied, setCopied] = useState(false);
  if (steps.length === 0) return null;
  return (
    <div className="space-y-2 rounded border border-warning p-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">需人工完成：</p>
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(formatManualSteps(steps, heading));
              setCopied(true);
            } catch {
              setCopied(false);
            }
          }}
        >
          {copied ? "已複製" : "複製步驟"}
        </Button>
      </div>
      <ol className="list-decimal space-y-1 pl-5">
        {steps.map((s, i) => (
          <li key={`${i}-${s}`} className="break-words">
            {s}
          </li>
        ))}
      </ol>
    </div>
  );
}

function RequestCard({ request: r, onApprove, onReject }: { request: FanPassConfigRequest; onApprove: () => void; onReject: () => void }) {
  const pending = r.status === "pending";
  const current = useCurrentFanPassValues(r.artistId, pending);
  const lines = fanPassChangeSummary(r, current.values);
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`${artistHref(r.artistId)}#fan-pass`} className="text-base font-semibold break-words text-primary underline underline-offset-2">
              {r.artistName || r.artistId}
            </Link>
            <Badge variant={fanPassRequestStatusVariant(r.status)}>{fanPassRequestStatusLabel(r.status)}</Badge>
          </div>
          <p className="text-xs text-muted-foreground break-all">
            送出時間：{fmt(r.createdAt)} · 申請人 <span className="font-mono">{r.uid}</span>
          </p>
          {pending && current.loading && <p className="text-xs text-muted-foreground">讀取目前設定…</p>}
          <ChangeSummary lines={lines} showFrom={pending} />
          {!pending && (
            <div className="space-y-1 rounded border p-2 text-sm">
              <p>
                審核：<span className="font-mono text-xs break-all">{r.reviewedBy || "—"}</span> · {fmt(r.reviewedAt)}
              </p>
              {r.status === "rejected" && r.reason && <p className="break-words">原因：{r.reason}</p>}
            </div>
          )}
          {r.status === "approved_manual" && (
            <ManualSteps steps={r.manualSteps ?? []} heading={`鐵粉月卡申請 ${r.requestId}（${r.artistName || r.artistId}）`} />
          )}
        </div>
        {pending && (
          <div className="flex shrink-0 gap-2 sm:flex-col">
            <Button onClick={onApprove}>核准</Button>
            <Button variant="outline" onClick={onReject}>
              拒絕
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function useRequestMutationDone() {
  const client = useQueryClient();
  return (artistId?: string) => {
    void client.invalidateQueries({ queryKey: ["fan-pass-requests"] });
    if (artistId) {
      for (const key of [["artist-fan-pass-config", artistId], ["artist-fan-product", artistId], ["dm-config", artistId], ["artist-audit", artistId]]) {
        void client.invalidateQueries({ queryKey: key });
      }
    }
  };
}

function ApproveDialog({ request, onClose, onDone }: { request: FanPassConfigRequest; onClose: () => void; onDone: (notice: string) => void }) {
  const refresh = useRequestMutationDone();
  const current = useCurrentFanPassValues(request.artistId, true);
  const lines = fanPassChangeSummary(request, current.values);
  const name = request.artistName || request.artistId;
  const approve = useMutation({
    mutationFn: () => fanPassConfigRequestApprove(request.requestId),
    onSuccess: (r) => {
      refresh(request.artistId);
      // approved_manual keeps the dialog open so staff can copy the manual steps.
      if (r.status === "approved") onDone(`已核准並套用「${name}」的月卡設定`);
    },
    onError: (e) => {
      if (isStateConflict(e)) refresh();
    },
  });
  const manual = approve.data?.status === "approved_manual" ? approve.data : null;
  return (
    <Dialog open onOpenChange={(open) => !open && !approve.isPending && (manual ? onDone(`已核准「${name}」，尚有人工步驟待完成（見「需人工」）`) : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>核准「{name}」的月卡申請</DialogTitle>
          <DialogDescription>
            {manual
              ? "已核准，但有部分設定無法自動套用，請依下列步驟手動完成。"
              : "核准後會套用下列變更（商店價格檔位變更只影響新訂閱者；iOS 價格變更可能需要重新送審）。"}
          </DialogDescription>
        </DialogHeader>
        {!manual && (
          <>
            {current.loading && <p className="text-xs text-muted-foreground">讀取目前設定…</p>}
            <ChangeSummary lines={lines} showFrom />
          </>
        )}
        {manual && (
          <>
            {manual.applied && <p className="text-sm">可自動套用的部分已完成。</p>}
            <ManualSteps steps={manual.manualSteps ?? []} heading={`鐵粉月卡申請 ${request.requestId}（${name}）`} />
          </>
        )}
        {approve.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorText(approve.error)}
          </p>
        )}
        <DialogFooter>
          {manual ? (
            <Button onClick={() => onDone(`已核准「${name}」，尚有人工步驟待完成（見「需人工」）`)}>完成</Button>
          ) : (
            <>
              <Button variant="outline" disabled={approve.isPending} onClick={onClose}>
                取消
              </Button>
              <Button disabled={approve.isPending || isStateConflict(approve.error)} onClick={() => approve.mutate()}>
                {approve.isPending ? "套用中…" : "確認核准"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ request, onClose, onDone }: { request: FanPassConfigRequest; onClose: () => void; onDone: () => void }) {
  const refresh = useRequestMutationDone();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const problem = rejectReasonProblem(reason);
  const reject = useMutation({
    mutationFn: () => fanPassConfigRequestReject(request.requestId, reason.trim()),
    onSuccess: () => {
      refresh();
      onDone();
    },
    onError: (e) => {
      if (isStateConflict(e)) refresh();
    },
  });
  return (
    <Dialog open onOpenChange={(open) => !open && !reject.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>拒絕「{request.artistName || request.artistId}」的月卡申請</DialogTitle>
          <DialogDescription>原因會顯示給創作者，請說明需要如何調整後再送出。</DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="fan-pass-reject-reason">拒絕原因（必填，至少 5 字）</Label>
          <Textarea
            id="fan-pass-reject-reason"
            rows={4}
            maxLength={REJECT_REASON_MAX * 2}
            value={reason}
            aria-invalid={touched && !!problem}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          {touched && problem && <p className="text-sm text-destructive">{problem}</p>}
        </div>
        {reject.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorText(reject.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={reject.isPending} onClick={onClose}>
            取消
          </Button>
          <Button variant="destructive" disabled={reject.isPending || !!problem || isStateConflict(reject.error)} onClick={() => reject.mutate()}>
            {reject.isPending ? "送出中…" : "確認拒絕"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
